import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBrands } from "../contexts/BrandContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { format as formatDate, parseISO as parseIsoDate } from "date-fns";
import { fr as frLocale } from "date-fns/locale";
import {
  Plus, Trash2, Pencil, TrendingUp, TrendingDown, CalendarDays, Receipt,
  RotateCcw, Contact, Briefcase, Tag, Target, BarChart3, LineChart as LineIcon,
  Percent, CreditCard, Landmark, Wallet, CheckCircle2, Clock, Users, Building2, Trophy,
  Coins,
} from "lucide-react";
import { toast } from "sonner";
import QuickSearchBar, { useQuickFilter } from "../components/QuickSearchBar";
import ExportCsvButton from "../components/ExportCsvButton";
import CommissionsTab from "../components/CommissionsTab";
import RoyaltiesSection from "../components/royalties/RoyaltiesSection";
import SearchableSelect from "../components/SearchableSelect";
import BeneficiaryCombobox from "../components/BeneficiaryCombobox";
import HorizontalScrollContainer from "../components/HorizontalScrollContainer";
import QuickBeneficiaryDialog from "../components/QuickBeneficiaryDialog";
import useIsMobile from "../hooks/useIsMobile";
import {
  ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";

const money = (v) => `${Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const shortMoney = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)}k€`;
  return `${n.toFixed(0)}€`;
};

const PERIODS = [
  { key: "day", label: "Jour" },
  { key: "week", label: "Semaine" },
  { key: "month", label: "Mois" },
  { key: "year", label: "Année" },
  { key: "custom", label: "Plage" },
];

const PALETTE = ["#F97316", "#0EA5E9", "#EC4899", "#059669", "#8B5CF6", "#DC2626", "#F59E0B", "#0284C7"];

const PAYMENT_METHODS = [
  { value: "cb", label: "CB", icon: CreditCard },
  { value: "transfer", label: "Virement bancaire", icon: Landmark },
  { value: "other", label: "Autre mode de paiement", icon: Wallet },
];
const PAYMENT_METHOD_LABEL = Object.fromEntries(PAYMENT_METHODS.map((m) => [m.value, m.label]));

export default function KpiTransactions() {
  const { user } = useAuth();
  const { brands, selectedBrandId } = useBrands();
  const canManage = ["admin", "manager"].includes(user?.role);
  // Le Gestionnaire peut créer une transaction (limité à ses départements
  // côté backend) mais ne peut ni modifier, ni supprimer, ni rembourser.
  const canCreate = ["admin", "manager", "gestionnaire"].includes(user?.role);
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const isGestionnaire = user?.role === "gestionnaire";
  // RBAC iter108 : un Gestionnaire n'accède qu'aux onglets « Transactions » et
  // « Commissions partenaires ». Le Tableau de bord, les Royalties et les
  // Objectifs sont masqués. Il ne peut ni créer, ni modifier, ni supprimer, ni
  // rembourser (interdiction remboursement — réservé aux Admin/Manager).
  const canSeeRoyalties = (isAdmin || isManager);
  const canSeeDashboard = !isGestionnaire;
  const canRefund = isAdmin || isManager;

  const [tab, setTab] = useState(isGestionnaire ? "transactions" : "dashboard");
  // Hydrate l'onglet actif depuis `?tab=` (utilisé quand on revient de
  // l'espace partenaire consulté par un admin/manager).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const wanted = searchParams.get("tab");
    const allowed = ["dashboard", "transactions", "commissions", "royalties", "objectifs"];
    if (wanted && allowed.includes(wanted) && wanted !== tab) {
      setTab(wanted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [list, setList] = useState([]);
  const [filters, setFilters] = useState({ q: "", brand_id: "", site_id: "", type: "" });
  // Filtre plage de dates pour l'onglet Transactions ({ from: Date | undefined, to: Date | undefined })
  const [txRange, setTxRange] = useState({ from: undefined, to: undefined });
  const isMobile = useIsMobile();

  // Dashboard state
  const [period, setPeriod] = useState("month");
  const [refDate, setRefDate] = useState(new Date().toISOString().slice(0, 10));
  // Plage personnalisée (période "custom") — { from: Date, to: Date }
  const todayIso = new Date().toISOString().slice(0, 10);
  const firstOfMonthIso = todayIso.slice(0, 8) + "01";
  const [refRange, setRefRange] = useState({ from: firstOfMonthIso, to: todayIso });
  const [dashFilters, setDashFilters] = useState({ brand_id: "", site_id: "", service_product_id: "", partner_id: "", code_value: "" });
  const [analytics, setAnalytics] = useState(null);
  const [monthlyPayouts, setMonthlyPayouts] = useState(null);
  const [goals, setGoals] = useState([]);

  // Contexts (partagés)
  const [allSites, setAllSites] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [allCollabs, setAllCollabs] = useState([]);
  const [allBeneficiaries, setAllBeneficiaries] = useState([]);

  // Form state
  const emptyForm = () => ({
    type: "vente",
    brand_id: "",
    site_id: "",
    service_product_id: "",
    date: new Date().toISOString().slice(0, 10),
    client_id: "",
    partner_id: "",        // bénéficiaire (contacts & ressources)
    collaborator_id: "",   // collaborateur (page collaborateurs)
    unit_price_ht: 0,
    tva_rate: 0,
    quantity: 1,
    // Champs manuels utilisés uniquement lorsqu'un service « Tarif sur devis »
    // est sélectionné (sinon repris automatiquement de la fiche service).
    fees_ht: 0,
    disbursements: 0,
    discount_code_kind: "none",
    discount_code_id: "",
    discount_code_value: "",
    discount_offer_id: "",         // offre sélectionnée (si code multi-offres)
    discount_type: "none",
    discount_value: 0,
    // Autre remise accordée — remise manuelle additionnelle appliquée par-dessus la remise du code (cumul sur le HT).
    extra_discount_type: "none",   // "none" | "percentage" | "amount"
    extra_discount_value: 0,
    extra_discount_note: "",       // motif libre (facultatif)
    notes: "",
    // Paiement — par défaut effectué le jour même par CB
    payment_status: "paid",
    payment_method: "cb",
    payment_reference: "",
    payment_date: new Date().toISOString().slice(0, 10),
    planned_payment_date: "",
    planned_payment_method: "transfer",
  });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [sites, setSites] = useState([]);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [collabs, setCollabs] = useState([]);
  const [activeCodes, setActiveCodes] = useState([]);
  const [codeStatus, setCodeStatus] = useState({ msg: "", cls: "" });
  const [codeLookup, setCodeLookup] = useState(false);
  // Cache de la dernière réponse `/transactions/resolve-code` (utile pour proposer les offres si le code a été saisi à la main).
  const [typedCodeCache, setTypedCodeCache] = useState(null);

  // Création rapide contact (client / partenaire) sans quitter la modale transaction.
  // `target` détermine dans quel champ le contact fraîchement créé sera sélectionné.
  const [quickContactOpen, setQuickContactOpen] = useState(false);
  const [quickContactTarget, setQuickContactTarget] = useState(null); // 'client' | 'partner'

  // Goals dialog
  const [goalOpen, setGoalOpen] = useState(false);
  const [goalEditing, setGoalEditing] = useState(null);
  const [goalForm, setGoalForm] = useState({ brand_id: "", period: "month", period_key: new Date().toISOString().slice(0, 7), target_amount: 0, notes: "" });

  // Sous-onglet transactions (« all » | « pending »)
  const [txSubTab, setTxSubTab] = useState("all");
  // Modale de confirmation d'un paiement différé
  const [confirmPayOpen, setConfirmPayOpen] = useState(false);
  const [confirmPayTx, setConfirmPayTx] = useState(null);
  const [confirmPayForm, setConfirmPayForm] = useState({
    payment_date: new Date().toISOString().slice(0, 10),
    payment_method: "cb",
    payment_reference: "",
  });

  // -- Loaders --
  const load = useCallback(async () => {
    try {
      const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
      const { data } = await api.get("/transactions", { params });
      setList(data || []);
    } catch { setList([]); }
  }, [selectedBrandId]);

  const loadAnalytics = useCallback(async () => {
    try {
      const params = { period };
      if (period === "custom") {
        if (!refRange.from || !refRange.to) return;
        params.date_from = refRange.from;
        params.date_to = refRange.to;
      } else {
        params.ref_date = refDate;
      }
      const brand = dashFilters.brand_id || selectedBrandId || "";
      if (brand) params.brand_id = brand;
      if (dashFilters.site_id) params.site_id = dashFilters.site_id;
      if (dashFilters.service_product_id) params.service_product_id = dashFilters.service_product_id;
      if (dashFilters.partner_id) params.partner_id = dashFilters.partner_id;
      if (dashFilters.code_value) params.code_value = dashFilters.code_value;
      const { data } = await api.get("/transactions/analytics", { params });
      setAnalytics(data);
    } catch { setAnalytics(null); }
  }, [period, refDate, refRange, dashFilters, selectedBrandId]);

  const loadGoals = useCallback(async () => {
    try {
      const brand = dashFilters.brand_id || selectedBrandId || "";
      const params = brand ? { brand_id: brand } : {};
      const { data } = await api.get("/revenue-goals", { params });
      setGoals(data || []);
    } catch { setGoals([]); }
  }, [dashFilters.brand_id, selectedBrandId]);

  const loadMonthlyPayouts = useCallback(async () => {
    if (!canSeeRoyalties) return;
    try {
      const { data } = await api.get("/kpi/monthly-payouts");
      setMonthlyPayouts(data);
    } catch { setMonthlyPayouts(null); }
  }, [canSeeRoyalties]);

  const loadGlobalContext = useCallback(async () => {
    try {
      const [s, sp, c, b] = await Promise.all([
        api.get("/sites"),
        api.get("/services-products"),
        api.get("/collaborateurs"),
        api.get("/beneficiaries"),
      ]);
      setAllSites(s.data || []);
      setAllServices(sp.data || []);
      setAllCollabs(c.data || []);
      setAllBeneficiaries(b.data || []);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); loadGlobalContext(); }, [load, loadGlobalContext]);
  useEffect(() => { loadAnalytics(); loadGoals(); loadMonthlyPayouts(); }, [loadAnalytics, loadGoals, loadMonthlyPayouts]);

  // -- Filtered list --
  const quickFiltered = useQuickFilter(list, filters, {
    text: (t) => `${t.client_name || ""} ${t.partner_name || ""} ${t.service_product_name || ""} ${t.discount_code_value || ""} ${t.notes || ""} ${t.payment_reference || ""}`,
    predicates: {
      brand_id: (t, v) => t.brand_id === v,
      site_id: (t, v) => t.site_id === v,
      type: (t, v) => t.type === v,
    },
  });
  // Application du filtre plage de dates (bornes incluses, comparaison ISO YYYY-MM-DD)
  const filtered = useMemo(() => {
    if (!txRange?.from && !txRange?.to) return quickFiltered;
    const fromIso = txRange.from ? formatDate(txRange.from, "yyyy-MM-dd") : null;
    const toIso = txRange.to ? formatDate(txRange.to, "yyyy-MM-dd") : (fromIso || null);
    return quickFiltered.filter((t) => {
      const d = (t.date || "").slice(0, 10);
      if (!d) return false;
      if (fromIso && d < fromIso) return false;
      if (toIso && d > toIso) return false;
      return true;
    });
  }, [quickFiltered, txRange]);

  // Séparation « toutes » vs « en attente de paiement » (paiement différé)
  const pendingPaymentList = useMemo(
    () => filtered.filter((t) => t.payment_status === "deferred"),
    [filtered],
  );
  const visibleRows = txSubTab === "pending" ? pendingPaymentList : filtered;

  const totals = useMemo(() => {
    const ventes = filtered.filter((t) => t.type === "vente");
    const refunds = filtered.filter((t) => t.type === "remboursement");
    const brut = ventes.reduce((a, t) => a + Number(t.amount_ttc || 0), 0);
    const refundsSum = refunds.reduce((a, t) => a + Math.abs(Number(t.amount_ttc || 0)), 0);
    const avg = ventes.length ? brut / ventes.length : 0;
    return { count: filtered.length, brut, refundsSum, avg, ventes: ventes.length, refunds: refunds.length };
  }, [filtered]);

  // -- Form helpers --
  const loadBrandContext = async (bid) => {
    setSites([]); setServices([]); setClients([]); setCollabs([]); setActiveCodes([]);
    if (!bid) return;
    api.get("/sites", { params: { brand_id: bid } }).then(({ data }) => setSites(data || [])).catch(() => {});
    api.get("/services-products", { params: { brand_id: bid } }).then(({ data }) => setServices(data || [])).catch(() => {});
    api.get("/beneficiaries", { params: { brand_id: bid } }).then(({ data }) => setClients(data || [])).catch(() => {});
    api.get("/collaborateurs", { params: { brand_id: bid } }).then(({ data }) => setCollabs(data || [])).catch(() => {});
    api.get("/transactions/active-codes", { params: { brand_id: bid } }).then(({ data }) => setActiveCodes(data || [])).catch(() => {});
  };

  const openCreate = () => {
    setEditing(null);
    const defaultBrand = selectedBrandId || (brands[0]?.id || "");
    setForm({ ...emptyForm(), brand_id: defaultBrand });
    setCodeStatus({ msg: "", cls: "" });
    if (defaultBrand) loadBrandContext(defaultBrand);
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    // Rétro-compat : anciens enregistrements avec partner_type=collaborator
    // migrent leur partner_id vers collaborator_id à l'ouverture du form.
    const isLegacyCol = t.partner_type === "collaborator" && t.partner_id;
    setForm({
      type: t.type || "vente",
      brand_id: t.brand_id || "",
      site_id: t.site_id || "",
      service_product_id: t.service_product_id || "",
      date: t.date || new Date().toISOString().slice(0, 10),
      client_id: t.client_id || "",
      partner_id: isLegacyCol ? "" : (t.partner_id || ""),
      collaborator_id: t.collaborator_id || (isLegacyCol ? t.partner_id : "") || "",
      unit_price_ht: Number(t.unit_price_ht || 0),
      tva_rate: Number(t.tva_rate || 0),
      quantity: Number(t.quantity || 1),
      fees_ht: Number(t.fees_ht || 0),
      disbursements: Number(t.disbursements || 0),
      discount_code_kind: t.discount_code_kind || "none",
      discount_code_id: t.discount_code_id || "",
      discount_code_value: t.discount_code_value || "",
      discount_offer_id: t.discount_offer_id || "",
      discount_type: t.discount_type || "none",
      discount_value: Number(t.discount_value || 0),
      extra_discount_type: t.extra_discount_type || "none",
      extra_discount_value: Number(t.extra_discount_value || 0),
      extra_discount_note: t.extra_discount_note || "",
      notes: t.notes || "",
      payment_status: t.payment_status || "paid",
      payment_method: t.payment_method || "cb",
      payment_reference: t.payment_reference || "",
      payment_date: t.payment_date || t.date || new Date().toISOString().slice(0, 10),
      planned_payment_date: t.planned_payment_date || "",
      planned_payment_method: t.planned_payment_method || "transfer",
    });
    setCodeStatus({ msg: "", cls: "" });
    if (t.brand_id) loadBrandContext(t.brand_id);
    setOpen(true);
  };

  const onServiceChange = (sid) => {
    const s = services.find((x) => x.id === sid);
    const isQuote = !!(s && s.tarif_sur_devis);
    setForm((f) => ({
      ...f,
      service_product_id: sid,
      // Service « Tarif sur devis » : les champs Prix HT / TVA / Frais / Débours
      // sont saisis manuellement — on réinitialise à 0 pour laisser l'utilisateur
      // renseigner les vraies valeurs (Prix HT et TVA obligatoires).
      unit_price_ht: isQuote ? 0 : (s ? Number(s.price_ht || 0) : f.unit_price_ht),
      tva_rate: isQuote ? 0 : (s ? Number(s.vat_rate || 0) : f.tva_rate),
      fees_ht: isQuote ? 0 : f.fees_ht,
      disbursements: isQuote ? 0 : f.disbursements,
    }));
  };

  const applyActiveCode = (codeId) => {
    if (codeId === "none") {
      setForm((f) => ({ ...f, discount_code_kind: "none", discount_code_id: "", discount_code_value: "", discount_offer_id: "", discount_type: "none", discount_value: 0 }));
      setCodeStatus({ msg: "", cls: "" });
      return;
    }
    const c = activeCodes.find((x) => `${x.kind}:${x.id}` === codeId);
    if (!c) return;
    applyCodeToForm(c);
  };

  // Applique un code au formulaire — gère les codes multi-offres.
  // Auto-remplit également le contact partenaire (`partner_id`) tiré de la
  // fiche du code, sauf si l'utilisateur a déjà renseigné un autre partenaire.
  const applyCodeToForm = (c) => {
    const offers = Array.isArray(c.offers) ? c.offers : [];
    const currentSid = form.service_product_id;
    const codeBenId = c.beneficiary_id || null;
    // Base commune + auto-fill partenaire (uniquement si le champ est vide,
    // pour ne pas écraser un choix explicite de l'utilisateur).
    setForm((f) => {
      const shouldFillPartner = codeBenId && !f.partner_id && !f.collaborator_id;
      return {
        ...f,
        discount_code_kind: c.kind,
        discount_code_id: c.id,
        discount_code_value: c.code,
        ...(shouldFillPartner ? { partner_id: codeBenId } : {}),
      };
    });
    if (offers.length === 0) {
      // Legacy — remise unique
      setForm((f) => ({
        ...f,
        discount_offer_id: "",
        discount_type: c.discount_type || "none",
        discount_value: Number(c.discount_value || 0),
      }));
      const partnerNote = codeBenId ? ` (partenaire lié : ${c.beneficiary_name || "auto-rempli"})` : "";
      setCodeStatus({ msg: `Code ${c.kind} appliqué${partnerNote}`, cls: "text-emerald-700" });
      return;
    }
    // Multi-offres : filtre les offres compatibles avec le service courant
    const compatible = offers.filter((o) => !(o.service_product_ids || []).length || (o.service_product_ids || []).includes(currentSid));
    const partnerNote = codeBenId ? ` — partenaire ${c.beneficiary_name || "lié"}` : "";
    if (compatible.length === 1) {
      const o = compatible[0];
      setForm((f) => ({
        ...f,
        discount_offer_id: o.id,
        discount_type: o.discount_type || "none",
        discount_value: Number(o.discount_value || 0),
      }));
      setCodeStatus({ msg: `Code ${c.kind} — offre auto-appliquée${partnerNote}`, cls: "text-emerald-700" });
    } else if (compatible.length === 0) {
      setForm((f) => ({ ...f, discount_offer_id: "", discount_type: "none", discount_value: 0 }));
      setCodeStatus({ msg: "Aucune offre de ce code n'est applicable à ce service", cls: "text-amber-700" });
    } else {
      // Plusieurs offres compatibles → l'utilisateur doit choisir
      setForm((f) => ({ ...f, discount_offer_id: "", discount_type: "none", discount_value: 0 }));
      setCodeStatus({ msg: `Ce code propose ${compatible.length} offres — sélectionnez celle à appliquer${partnerNote}`, cls: "text-amber-700" });
    }
  };

  // Sélection manuelle d'une offre parmi celles du code actuellement choisi.
  const applyPickedOffer = (offerId) => {
    const codeId = form.discount_code_id;
    if (!codeId) return;
    const c = activeCodes.find((x) => x.id === codeId) || (typedCodeCache && typedCodeCache.id === codeId ? typedCodeCache : null);
    if (!c) return;
    const o = (c.offers || []).find((x) => x.id === offerId);
    if (!o) {
      setForm((f) => ({ ...f, discount_offer_id: "", discount_type: "none", discount_value: 0 }));
      return;
    }
    setForm((f) => ({
      ...f,
      discount_offer_id: o.id,
      discount_type: o.discount_type || "none",
      discount_value: Number(o.discount_value || 0),
    }));
    setCodeStatus({ msg: `Offre « ${o.label || "sans nom"} » appliquée`, cls: "text-emerald-700" });
  };

  const resolveTypedCode = async (raw) => {
    const code = (raw || "").trim().toUpperCase();
    if (!code || !form.brand_id) return;
    setCodeLookup(true);
    try {
      const { data } = await api.get("/transactions/resolve-code", { params: { code, brand_id: form.brand_id } });
      if (!data.is_active) {
        setForm((f) => ({
          ...f,
          discount_code_kind: data.kind,
          discount_code_id: data.id,
          discount_code_value: data.code,
          discount_offer_id: "",
          discount_type: "none",
          discount_value: 0,
        }));
        setCodeStatus({ msg: "Code trouvé mais INACTIF", cls: "text-amber-700" });
        setTypedCodeCache(data);
        return;
      }
      setTypedCodeCache(data);
      applyCodeToForm(data);
    } catch (e) {
      // Réinitialise TOUTES les métadonnées du code (y compris la valeur saisie)
      // pour éviter un 404 lors de l'enregistrement.
      setForm((f) => ({
        ...f,
        discount_code_kind: "none",
        discount_code_id: "",
        discount_code_value: "",
        discount_offer_id: "",
        discount_type: "none",
        discount_value: 0,
      }));
      setTypedCodeCache(null);
      setCodeStatus({ msg: e.response?.data?.detail || "Code introuvable", cls: "text-red-600" });
    } finally { setCodeLookup(false); }
  };

  const previewTTC = useMemo(() => {
    const qty = Number(form.quantity || 1);
    const ht = Number(form.unit_price_ht || 0) * qty;
    const tva = Number(form.tva_rate || 0);
    const clamp = (v, min, max) => Math.min(Math.max(v, min), max);
    const compute = (t, v) => {
      const val = Number(v || 0);
      if (t === "percentage") return ht * clamp(val, 0, 100) / 100;
      if (t === "amount") return Math.max(val, 0);
      return 0;
    };
    const dCode = compute(form.discount_type, form.discount_value);
    const dExtra = compute(form.extra_discount_type, form.extra_discount_value);
    const d = Math.min(dCode + dExtra, ht);
    // Formule alignée backend (_compute_amount) : TVA = HT × tva% (non impactée
    // par la remise, cf. règle métier CA net simplifié). Le TTC client est donc
    // (HT − remise) + TVA(HT). Sans cet alignement, la prévisualisation
    // affichait un TTC inférieur au montant réellement enregistré.
    const net = Math.max(ht - d, 0);
    const tvaAmount = ht * tva / 100;
    let ttc = net + tvaAmount;
    if (form.type === "remboursement") ttc = -Math.abs(ttc);
    return { ht, discount: d, discount_code: Math.min(dCode, ht), discount_extra: Math.min(dExtra, Math.max(ht - dCode, 0)), ttc };
  }, [form.unit_price_ht, form.quantity, form.tva_rate, form.discount_type, form.discount_value, form.extra_discount_type, form.extra_discount_value, form.type]);

  const save = async () => {
    if (!form.brand_id) return toast.error("Département requis");
    if (!form.site_id) return toast.error("Site requis");
    if (!form.service_product_id) return toast.error("Service / produit requis");
    if (!form.client_id) return toast.error("Client requis");
    // Validation « Tarif sur devis » — Prix HT et TVA sont obligatoires (les
    // Frais HT et Débours restent optionnels).
    const selectedService = services.find((s) => s.id === form.service_product_id);
    const svcIsQuote = !!(selectedService && selectedService.tarif_sur_devis);
    if (svcIsQuote) {
      const htVal = Number(form.unit_price_ht);
      const tvaVal = Number(form.tva_rate);
      if (!Number.isFinite(htVal) || htVal <= 0) return toast.error("Prix HT requis pour ce service au tarif sur devis");
      if (!Number.isFinite(tvaVal) || tvaVal < 0 || tvaVal > 100) return toast.error("TVA (%) valide requise (0 à 100) pour ce service au tarif sur devis");
      const feesVal = Number(form.fees_ht || 0);
      const disbVal = Number(form.disbursements || 0);
      if (!Number.isFinite(feesVal) || feesVal < 0) return toast.error("Montant HT des frais facturés invalide");
      if (!Number.isFinite(disbVal) || disbVal < 0) return toast.error("Montant des débours facturés invalide");
    }
    // Règle métier : sur un site « Plateforme en ligne », partenaire et
    // collaborateur sont optionnels — SAUF si un code de remise (promo,
    // parrainage, affiliation) est saisi, car chaque code est forcément
    // rattaché à un collaborateur ou à un partenaire.
    const selectedSite = sites.find((s) => s.id === form.site_id);
    const siteIsOnlinePlatform = selectedSite?.type === "plateforme_en_ligne";
    const hasDiscountCode = !!(form.discount_code_value || "").trim();
    const partnerOrCollabRequired = !siteIsOnlinePlatform || hasDiscountCode;
    if (partnerOrCollabRequired && !form.partner_id && !form.collaborator_id) {
      return toast.error(siteIsOnlinePlatform
        ? "Un partenaire ou un collaborateur est requis dès qu'un code de remise est utilisé"
        : "Au moins un partenaire ou un collaborateur est requis");
    }
    if (form.type !== "remboursement") {
      if (form.payment_status === "paid") {
        if (!form.payment_method) return toast.error("Mode de paiement requis");
        if (!form.payment_date) return toast.error("Date de paiement requise");
      } else if (form.payment_status === "deferred") {
        if (!form.planned_payment_date) return toast.error("Date de règlement prévue requise");
        if (!form.planned_payment_method) return toast.error("Mode de règlement prévu requis");
      }
    }
    try {
      const isDeferred = form.type !== "remboursement" && form.payment_status === "deferred";
      const payload = {
        ...form,
        partner_type: "beneficiary",
        partner_id: form.partner_id || null,
        collaborator_id: form.collaborator_id || null,
        unit_price_ht: Number(form.unit_price_ht || 0),
        tva_rate: Number(form.tva_rate || 0),
        quantity: Number(form.quantity || 1),
        // Frais / Débours manuels — envoyés uniquement pour un service sur devis
        // (côté backend, ils sont ignorés hors mode devis).
        fees_ht: svcIsQuote ? Number(form.fees_ht || 0) : undefined,
        disbursements: svcIsQuote ? Number(form.disbursements || 0) : undefined,
        discount_value: Number(form.discount_value || 0),
        discount_code_id: form.discount_code_id || null,
        discount_code_value: form.discount_code_value || null,
        discount_offer_id: form.discount_offer_id || null,
        extra_discount_type: form.extra_discount_type || "none",
        extra_discount_value: Number(form.extra_discount_value || 0),
        extra_discount_note: (form.extra_discount_note || "").trim() || null,
        payment_status: form.type === "remboursement" ? "paid" : form.payment_status,
        payment_method: isDeferred ? null : (form.payment_method || null),
        payment_reference: isDeferred ? null : ((form.payment_reference || "").trim() || null),
        payment_date: isDeferred ? null : (form.payment_date || null),
        planned_payment_date: isDeferred ? (form.planned_payment_date || null) : null,
        planned_payment_method: isDeferred ? (form.planned_payment_method || null) : null,
      };
      if (editing) {
        await api.put(`/transactions/${editing.id}`, payload);
        toast.success("Transaction mise à jour");
      } else {
        await api.post("/transactions", payload);
        toast.success("Transaction enregistrée");
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyForm());
      load();
      loadAnalytics();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const refund = async (t) => {
    const amt = (t.amount_ttc || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const msg = `Créer un remboursement pour la transaction du ${t.date} (${amt} €) ?\n\n`
      + `L'effet comptable (CA, commissions, royalties, primes) sera attribué à la date d'origine `
      + `(${t.date}) — la période initiale sera corrigée, la période courante ne sera pas impactée.`;
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/transactions/${t.id}/refund`, {});
      toast.success("Remboursement enregistré à la date de la transaction d'origine");
      load();
      loadAnalytics();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors du remboursement"); }
  };

  const openConfirmPayment = (t) => {
    setConfirmPayTx(t);
    setConfirmPayForm({
      // Date effective : par défaut aujourd'hui (ne pas pré-remplir avec la date prévue
      // pour éviter d'enregistrer un paiement futur par mégarde).
      payment_date: new Date().toISOString().slice(0, 10),
      payment_method: t.planned_payment_method || "cb",
      payment_reference: "",
    });
    setConfirmPayOpen(true);
  };

  const submitConfirmPayment = async () => {
    if (!confirmPayTx) return;
    if (!confirmPayForm.payment_date) return toast.error("Date effective de paiement requise");
    if (!confirmPayForm.payment_method) return toast.error("Mode de paiement effectif requis");
    try {
      await api.post(`/transactions/${confirmPayTx.id}/confirm-payment`, {
        payment_date: confirmPayForm.payment_date,
        payment_method: confirmPayForm.payment_method,
        payment_reference: (confirmPayForm.payment_reference || "").trim() || null,
      });
      toast.success("Paiement confirmé — transaction basculée vers « Transactions »");
      setConfirmPayOpen(false);
      setConfirmPayTx(null);
      load();
      loadAnalytics();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la confirmation");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Supprimer définitivement la transaction du ${t.date} ?`)) return;
    try {
      await api.delete(`/transactions/${t.id}`);
      toast.success("Transaction supprimée");
      load();
      loadAnalytics();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // -- Goals --
  const openGoalCreate = () => {
    setGoalEditing(null);
    const defaultBrand = dashFilters.brand_id || selectedBrandId || (brands[0]?.id || "");
    setGoalForm({
      brand_id: defaultBrand,
      period: "month",
      period_key: new Date().toISOString().slice(0, 7),
      target_amount: 0,
      notes: "",
    });
    setGoalOpen(true);
  };

  const openGoalEdit = (g) => {
    setGoalEditing(g);
    setGoalForm({ brand_id: g.brand_id, period: g.period, period_key: g.period_key, target_amount: g.target_amount, notes: g.notes || "" });
    setGoalOpen(true);
  };

  const saveGoal = async () => {
    if (!goalForm.brand_id) return toast.error("Département requis");
    try {
      if (goalEditing) {
        await api.put(`/revenue-goals/${goalEditing.id}`, { target_amount: Number(goalForm.target_amount), notes: goalForm.notes });
        toast.success("Objectif mis à jour");
      } else {
        await api.post("/revenue-goals", { ...goalForm, target_amount: Number(goalForm.target_amount) });
        toast.success("Objectif créé");
      }
      setGoalOpen(false);
      loadGoals();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const removeGoal = async (g) => {
    if (!window.confirm(`Supprimer l'objectif ${g.period_key} (${g.brand_name}) ?`)) return;
    try {
      await api.delete(`/revenue-goals/${g.id}`);
      toast.success("Objectif supprimé");
      loadGoals();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // -- Progression objectifs vs période courante --
  const goalProgress = useMemo(() => {
    if (!analytics) return [];
    const key = period === "year"
      ? String(new Date(refDate).getFullYear())
      : (period === "month" ? refDate.slice(0, 7) : null);
    if (!key) return [];
    const wantedPeriod = period === "year" ? "year" : "month";
    const active = goals.filter((g) => g.period === wantedPeriod && g.period_key === key);
    return active.map((g) => {
      const brandRow = analytics.by_brand?.find((b) => b.id === g.brand_id);
      const achieved = brandRow ? brandRow.net : 0;
      const pct = g.target_amount > 0 ? Math.min((achieved / g.target_amount) * 100, 999) : 0;
      return { ...g, achieved, pct };
    });
  }, [goals, analytics, period, refDate]);

  // -- Chart data --
  const byBrandData = (analytics?.by_brand || []).slice(0, 6).map((b) => ({ name: b.name || "—", value: Math.max(b.net, 0) }));

  const growthPositive = (analytics?.growth || 0) >= 0;

  return (
    <div className="space-y-6" data-testid="kpi-transactions-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">Module KPI</p>
          <h1 className="text-3xl font-semibold text-zinc-900 inline-flex items-center gap-2">
            <TrendingUp size={22} className="text-[#F97316]" /> KPI & Transactions
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Suivez ventes, remboursements, remises et progression vers vos objectifs.
          </p>
        </div>
        {canCreate && (
          <Button onClick={openCreate} className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-sm" data-testid="new-transaction-btn">
            <Plus size={14} className="mr-1" /> Enregistrer une transaction
          </Button>
        )}
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="w-full overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="rounded-sm w-max">
            {canSeeDashboard && (
              <TabsTrigger value="dashboard" data-testid="tab-dashboard" className="inline-flex items-center gap-1.5">
                <LineIcon size={13} /> Tableau de bord
              </TabsTrigger>
            )}
            <TabsTrigger value="transactions" data-testid="tab-transactions" className="inline-flex items-center gap-1.5">
              <Receipt size={13} /> Transactions
            </TabsTrigger>
            <TabsTrigger value="commissions" data-testid="tab-commissions" className="inline-flex items-center gap-1.5">
              <Percent size={13} /> Commissions
            </TabsTrigger>
            {canSeeRoyalties && (
              <TabsTrigger value="royalties" data-testid="tab-royalties" className="inline-flex items-center gap-1.5">
                <Coins size={13} /> Royalties
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="goals" data-testid="tab-goals" className="inline-flex items-center gap-1.5">
                <Target size={13} /> Objectifs
              </TabsTrigger>
            )}
          </TabsList>
        </div>

        {/* ─── DASHBOARD ─── */}
        <TabsContent value="dashboard" className="space-y-6 mt-4">
          {/* Sélecteur de période */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="inline-flex rounded-sm border border-zinc-200 overflow-hidden" data-testid="period-toggle">
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPeriod(p.key)}
                  className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border-l first:border-l-0 border-zinc-200 ${period === p.key ? "bg-[#F97316] text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
                  data-testid={`period-${p.key}`}
                >{p.label}</button>
              ))}
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="rounded-sm font-normal text-xs" data-testid="ref-date-btn">
                  <CalendarDays size={13} className="mr-1.5 text-zinc-500" />
                  {period === "custom" ? (
                    refRange.from && refRange.to
                      ? `${formatDate(parseIsoDate(refRange.from), "dd MMM yyyy", { locale: frLocale })} → ${formatDate(parseIsoDate(refRange.to), "dd MMM yyyy", { locale: frLocale })}`
                      : "Choisir une plage"
                  ) : (
                    formatDate(parseIsoDate(refDate), "dd MMM yyyy", { locale: frLocale })
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-auto max-w-[95vw] overflow-auto" align="start" data-testid="ref-date-popover">
                {period === "custom" ? (
                  <Calendar
                    mode="range"
                    numberOfMonths={isMobile ? 1 : 2}
                    locale={frLocale}
                    selected={{
                      from: refRange.from ? parseIsoDate(refRange.from) : undefined,
                      to: refRange.to ? parseIsoDate(refRange.to) : undefined,
                    }}
                    onSelect={(r) => {
                      if (!r) { setRefRange({ from: "", to: "" }); return; }
                      setRefRange({
                        from: r.from ? formatDate(r.from, "yyyy-MM-dd") : "",
                        to: r.to ? formatDate(r.to, "yyyy-MM-dd") : (r.from ? formatDate(r.from, "yyyy-MM-dd") : ""),
                      });
                    }}
                    initialFocus
                    data-testid="ref-date-calendar-range"
                  />
                ) : (
                  <Calendar
                    mode="single"
                    locale={frLocale}
                    selected={parseIsoDate(refDate)}
                    onSelect={(d) => d && setRefDate(formatDate(d, "yyyy-MM-dd"))}
                    initialFocus
                    data-testid="ref-date-calendar-single"
                  />
                )}
              </PopoverContent>
            </Popover>
            <div className="flex-1" />
            {/* Filtres avancés */}
            <Select value={dashFilters.brand_id || "all"} onValueChange={(v) => setDashFilters({ ...dashFilters, brand_id: v === "all" ? "" : v, site_id: "", service_product_id: "" })}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="dash-brand-filter"><SelectValue placeholder="Département" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous départements</SelectItem>
                {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={dashFilters.site_id || "all"} onValueChange={(v) => setDashFilters({ ...dashFilters, site_id: v === "all" ? "" : v })}>
              <SelectTrigger className="w-32 h-8 text-xs" data-testid="dash-site-filter"><SelectValue placeholder="Site" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous sites</SelectItem>
                {allSites.filter((s) => !dashFilters.brand_id || (s.brand_ids || []).includes(dashFilters.brand_id)).map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={dashFilters.service_product_id || "all"} onValueChange={(v) => setDashFilters({ ...dashFilters, service_product_id: v === "all" ? "" : v })}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="dash-service-filter"><SelectValue placeholder="Service" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous services</SelectItem>
                {allServices.filter((s) => !dashFilters.brand_id || (s.brand_ids || []).includes(dashFilters.brand_id)).map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={dashFilters.partner_id || "all"} onValueChange={(v) => setDashFilters({ ...dashFilters, partner_id: v === "all" ? "" : v })}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="dash-partner-filter"><SelectValue placeholder="Collaborateur" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous collaborateurs</SelectItem>
                {allCollabs.filter((c) => !dashFilters.brand_id || (c.brand_ids || []).includes(dashFilters.brand_id)).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Code…"
              value={dashFilters.code_value}
              onChange={(e) => setDashFilters({ ...dashFilters, code_value: e.target.value.toUpperCase() })}
              className="w-28 h-8 text-xs font-mono"
              data-testid="dash-code-filter"
            />
          </div>

          {/* Cartes comparaison période */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <ComparisonCard
              label="CA net"
              current={analytics?.current?.net || 0}
              previous={analytics?.previous?.net || 0}
              serverPct={analytics?.growth}
              tint="#F97316"
              testid="kpi-net"
            />
            <ComparisonCard
              label="CA brut"
              current={analytics?.current?.brut || 0}
              previous={analytics?.previous?.brut || 0}
              tint="#0EA5E9"
              testid="kpi-brut"
            />
            <ComparisonCard
              label="CA TTC"
              current={analytics?.current?.ttc || 0}
              previous={analytics?.previous?.ttc || 0}
              tint="#6366F1"
              testid="kpi-ttc"
            />
            <ComparisonCard
              label="Ventes"
              current={analytics?.current?.sales || 0}
              previous={analytics?.previous?.sales || 0}
              tint="#059669"
              testid="kpi-sales"
            />
            <ComparisonCard
              label="Remboursements"
              current={analytics?.current?.refunds || 0}
              previous={analytics?.previous?.refunds || 0}
              tint="#DC2626"
              invert
              testid="kpi-refunds"
            />
            <div className="bg-white border border-zinc-200 rounded-sm p-4 flex flex-col justify-between" data-testid="kpi-growth" style={{ borderLeft: `3px solid ${growthPositive ? "#059669" : "#DC2626"}` }}>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Croissance</p>
              <div className="inline-flex items-center gap-2 mt-1">
                {growthPositive ? <TrendingUp size={18} className="text-emerald-600" /> : <TrendingDown size={18} className="text-red-600" />}
                <p className="text-2xl font-mono font-semibold" style={{ color: growthPositive ? "#059669" : "#DC2626" }}>
                  {growthPositive ? "+" : ""}{analytics?.growth ?? 0}%
                </p>
              </div>
              <p className="text-[10px] text-zinc-500 font-mono mt-2">
                vs période précédente
              </p>
            </div>
          </div>

          {/* Rubriques financières globales — TVA / Frais / Débours / Remises */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kpi-fin-breakdown">
            <FinancialCard
              label="TVA encaissée"
              amount={analytics?.current?.tva || 0}
              previous={analytics?.previous?.tva || 0}
              icon={<Percent size={14} />}
              accent="#6366F1"
              testid="kpi-tva-encaissee"
              helper="Cumul net sur la période"
            />
            <FinancialCard
              label="Frais facturés"
              amount={analytics?.current?.fees || 0}
              previous={analytics?.previous?.fees || 0}
              icon={<Receipt size={14} />}
              accent="#0EA5E9"
              testid="kpi-fees-total"
              helper="Frais HT refacturés"
            />
            <FinancialCard
              label="Débours facturés"
              amount={analytics?.current?.disbursements || 0}
              previous={analytics?.previous?.disbursements || 0}
              icon={<Wallet size={14} />}
              accent="#0F766E"
              testid="kpi-disbursements-total"
              helper="Avances refacturées HT"
            />
            <FinancialCard
              label="Remises accordées"
              amount={analytics?.current?.discounts || 0}
              previous={analytics?.previous?.discounts || 0}
              icon={<Tag size={14} />}
              accent="#DC2626"
              testid="kpi-discounts-total"
              helper="Total réductions clients"
            />
          </div>

          {/* Widgets « Dus ce mois » — royalties + commissions (temps réel) */}
          {canSeeRoyalties && monthlyPayouts && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="monthly-payouts-widgets">
              <PayoutCard
                label="Commissions dues ce mois"
                amount={monthlyPayouts.commissions?.amount_ht || 0}
                sub={`${monthlyPayouts.commissions?.count || 0} commission${(monthlyPayouts.commissions?.count || 0) > 1 ? "s" : ""} — cumul temps réel`}
                icon={<Percent size={14} />}
                accent="#8B5CF6"
                testid="payout-commissions"
              />
              <PayoutCard
                label="Royalties dues ce mois"
                amount={monthlyPayouts.royalties?.amount_ht || 0}
                sub={`${monthlyPayouts.royalties?.beneficiaries || 0} bénéficiaire${(monthlyPayouts.royalties?.beneficiaries || 0) > 1 ? "s" : ""} actif${(monthlyPayouts.royalties?.beneficiaries || 0) > 1 ? "s" : ""} — cumul temps réel`}
                icon={<Coins size={14} />}
                accent="#F97316"
                testid="payout-royalties"
              />
            </div>
          )}

          {/* Objectifs progression */}
          {goalProgress.length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-sm p-4 space-y-3" data-testid="goals-progress">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-zinc-800">
                  <Target size={14} className="text-[#F97316]" /> Objectifs {period === "year" ? "annuels" : "mensuels"}
                </div>
                {(dashFilters.site_id || dashFilters.service_product_id || dashFilters.partner_id || dashFilters.code_value) && (
                  <span className="text-[10px] font-mono uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-0.5" data-testid="goals-filter-warning">
                    Progression filtrée
                  </span>
                )}
              </div>
              <div className="space-y-3">
                {goalProgress.map((g) => (
                  <div key={g.id} data-testid={`goal-progress-${g.id}`}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="inline-flex items-center gap-1.5 text-zinc-700 font-medium">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: g.brand_color || "#F97316" }} />
                        {g.brand_name} — {g.period_key}
                      </span>
                      <span className="font-mono text-zinc-500">
                        {money(g.achieved)} / {money(g.target_amount)} · <span className={g.pct >= 100 ? "text-emerald-600 font-semibold" : "text-zinc-600"}>{g.pct.toFixed(0)}%</span>
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${Math.min(g.pct, 100)}%`, backgroundColor: g.pct >= 100 ? "#059669" : g.brand_color || "#F97316" }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Transactions par département · Top 3 collaborateurs */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Transactions par département */}
            <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid="tx-by-department">
              <div className="flex items-center gap-2 text-sm font-semibold text-zinc-800 mb-3">
                <BarChart3 size={14} className="text-[#0EA5E9]" /> Transactions par département
              </div>
              {(analytics?.by_brand || []).length === 0 ? (
                <p className="text-xs text-zinc-400 font-mono py-6 text-center">Aucune transaction sur cette période</p>
              ) : (
                <ul className="space-y-2">
                  {(analytics?.by_brand || []).map((b, i) => {
                    const totalCount = (analytics?.by_brand || []).reduce((a, x) => a + (x.count || 0), 0) || 1;
                    const pct = ((b.count || 0) / totalCount) * 100;
                    return (
                      <li key={b.id} data-testid={`tx-dept-row-${b.id}`}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="inline-flex items-center gap-2 text-zinc-700 font-medium truncate">
                            <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: PALETTE[i % PALETTE.length] }} />
                            <span className="truncate">{b.name}</span>
                          </span>
                          <span className="font-mono text-zinc-600 flex-shrink-0 ml-2">{b.count}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: PALETTE[i % PALETTE.length] }} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Top 3 collaborateurs (par nombre de ventes) */}
            <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid="top-collaborators">
              <div className="flex items-center gap-2 text-sm font-semibold text-zinc-800 mb-3">
                <Users size={14} className="text-[#8B5CF6]" /> Top 3 collaborateurs
                <span className="text-[10px] text-zinc-400 font-mono ml-auto">nb ventes</span>
              </div>
              {(analytics?.top_collaborators || []).length === 0 ? (
                <p className="text-xs text-zinc-400 font-mono py-6 text-center">Aucune vente sur cette période</p>
              ) : (
                <ul className="space-y-2">
                  {(analytics?.top_collaborators || []).map((c, i) => {
                    const rankColors = ["#F59E0B", "#9CA3AF", "#B45309"];
                    return (
                      <li
                        key={c.id}
                        className="flex items-center gap-3 py-2 px-2 rounded-sm hover:bg-zinc-50 border border-transparent hover:border-zinc-100 transition-colors"
                        data-testid={`top-collab-row-${c.id}`}
                      >
                        <span
                          className="flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-mono font-bold text-white"
                          style={{ backgroundColor: rankColors[i] || "#71717a" }}
                        >
                          {i + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-zinc-800 truncate">{c.name}</p>
                          <p className="text-[10px] font-mono text-zinc-500">{money(c.net)} CA ventes</p>
                        </div>
                        <span className="font-mono text-sm font-semibold text-zinc-800">{c.count}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          {/* Top sites (pleine largeur — tableau, même modèle que Top codes) */}
          <div className="bg-white border border-zinc-200 rounded-sm p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-zinc-800 mb-3">
              <Trophy size={14} className="text-[#F97316]" /> Top sites
              <span className="text-[10px] text-zinc-400 font-mono ml-auto">CA HT · ventes</span>
            </div>
            <div className="overflow-x-auto" data-testid="top-sites-table">
              <table className="w-full text-sm min-w-[420px]">
                <thead className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono border-b border-zinc-200">
                  <tr>
                    <th className="text-left py-1.5 pr-2 w-12">Rang</th>
                    <th className="text-left py-1.5 pr-3">Site</th>
                    <th className="text-right py-1.5 px-3 whitespace-nowrap">Ventes</th>
                    <th className="text-right py-1.5 pl-3 whitespace-nowrap">CA HT</th>
                  </tr>
                </thead>
                <tbody>
                  {(analytics?.top_sites || []).length === 0 && (
                    <tr><td colSpan={4} className="py-4 text-center text-zinc-400 font-mono text-xs">Aucune donnée sur cette période</td></tr>
                  )}
                  {(analytics?.top_sites || []).map((s, i) => {
                    const rankColors = ["#F59E0B", "#9CA3AF", "#B45309"];
                    return (
                      <tr
                        key={s.id}
                        className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors"
                        data-testid={`top-site-row-${s.id}`}
                      >
                        <td className="py-1.5 pr-2">
                          <span
                            className="inline-flex h-5 w-5 rounded-full items-center justify-center text-[10px] font-mono font-bold text-white"
                            style={{ backgroundColor: rankColors[i] || "#71717a" }}
                          >
                            {i + 1}
                          </span>
                        </td>
                        <td className="py-1.5 pr-3">
                          <div className="flex items-center gap-1.5 max-w-[140px] sm:max-w-[220px] md:max-w-[320px]">
                            <Building2 size={11} className="text-zinc-400 flex-shrink-0" />
                            <span className="truncate text-xs font-medium text-zinc-800">{s.name}</span>
                          </div>
                        </td>
                        <td className="py-1.5 px-3 text-right font-mono text-xs whitespace-nowrap">{s.sales_count}</td>
                        <td className="py-1.5 pl-3 text-right font-mono text-xs font-semibold whitespace-nowrap">{money(s.ht)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Répartition par département + par service */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-zinc-200 rounded-sm p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-zinc-800 mb-3">
                <BarChart3 size={14} className="text-[#0EA5E9]" /> Répartition par département
              </div>
              <div style={{ width: "100%", height: 280 }} data-testid="chart-by-brand">
                <ResponsiveContainer>
                  <PieChart margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
                    <Pie
                      data={byBrandData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="45%"
                      outerRadius="70%"
                      labelLine={false}
                    >
                      {byBrandData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => money(v)} contentStyle={{ fontSize: 11, borderRadius: 2 }} />
                    <Legend
                      verticalAlign="bottom"
                      align="center"
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{ fontSize: 11, paddingTop: 4, lineHeight: "16px" }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-sm p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-zinc-800 mb-3">
                <BarChart3 size={14} className="text-[#F97316]" /> Top services / produits
              </div>
              <div style={{ width: "100%", height: 240 }} data-testid="chart-by-service">
                <ResponsiveContainer>
                  <BarChart data={(analytics?.by_service || []).slice(0, 6)} layout="vertical" margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis type="number" tick={{ fontSize: 10, fill: "#71717a" }} tickFormatter={shortMoney} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#71717a" }} width={100} />
                    <Tooltip formatter={(v) => money(v)} contentStyle={{ fontSize: 11, borderRadius: 2 }} />
                    <Bar dataKey="net" fill="#F97316" radius={[0, 2, 2, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Top codes utilisés */}
          <div className="bg-white border border-zinc-200 rounded-sm p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-zinc-800 mb-3">
              <Tag size={14} className="text-[#EC4899]" /> Top codes de remise utilisés
            </div>
            <div className="overflow-x-auto" data-testid="top-codes-table">
              <table className="w-full text-sm min-w-[420px]">
                <thead className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono border-b border-zinc-200">
                  <tr>
                    <th className="text-left py-1.5 pr-3 whitespace-nowrap">Code</th>
                    <th className="text-left py-1.5 pr-3 whitespace-nowrap">Type</th>
                    <th className="text-right py-1.5 px-3 whitespace-nowrap">Utilisations</th>
                    <th className="text-right py-1.5 pl-3 whitespace-nowrap">CA net généré</th>
                  </tr>
                </thead>
                <tbody>
                  {(analytics?.top_codes || []).length === 0 && (
                    <tr><td colSpan={4} className="py-4 text-center text-zinc-400 font-mono text-xs">Aucun code utilisé sur cette période</td></tr>
                  )}
                  {(analytics?.top_codes || []).map((c) => (
                    <tr key={`${c.kind}:${c.code}`} className="border-b border-zinc-100">
                      <td className="py-1.5 pr-3 font-mono text-xs whitespace-nowrap">{c.code}</td>
                      <td className="py-1.5 pr-3 whitespace-nowrap"><span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm bg-zinc-100 border border-zinc-200">{c.kind}</span></td>
                      <td className="py-1.5 px-3 text-right font-mono text-xs whitespace-nowrap">{c.count}</td>
                      <td className="py-1.5 pl-3 text-right font-mono text-xs font-semibold whitespace-nowrap">{money(c.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* ─── TRANSACTIONS LIST ─── */}
        <TabsContent value="transactions" className="space-y-4 mt-4">
          {/* Sous-onglets : Toutes / En attente de paiement */}
          <div className="inline-flex rounded-sm border border-zinc-200 bg-white p-0.5" data-testid="tx-sub-tabs">
            <button
              type="button"
              onClick={() => setTxSubTab("all")}
              data-testid="tx-subtab-all"
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded-sm transition-colors ${
                txSubTab === "all" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"
              }`}
            >
              <Receipt size={11} className="inline-block mr-1.5 -mt-0.5" />
              Toutes
              <span className="ml-1.5 text-[10px] opacity-70">({filtered.length})</span>
            </button>
            <button
              type="button"
              onClick={() => setTxSubTab("pending")}
              data-testid="tx-subtab-pending"
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded-sm transition-colors ${
                txSubTab === "pending" ? "bg-amber-600 text-white" : "text-zinc-600 hover:bg-amber-50"
              }`}
            >
              <Clock size={11} className="inline-block mr-1.5 -mt-0.5" />
              En attente de paiement
              <span className="ml-1.5 text-[10px] opacity-70">({pendingPaymentList.length})</span>
            </button>
          </div>

          <QuickSearchBar
            value={filters}
            onChange={setFilters}
            placeholder="Rechercher client, service, code, réf. paiement…"
            testid="transactions-search"
            filters={[
              { key: "type", label: "Type", type: "select", placeholder: "Tous", options: [
                { value: "vente", label: "Ventes" },
                { value: "remboursement", label: "Remboursements" },
              ] },
              { key: "brand_id", label: "Département", type: "select", placeholder: "Tous", options: brands.map((b) => ({ value: b.id, label: b.name })) },
            ]}
            renderExtraFilters={() => (
              <div className="flex flex-col gap-1" data-testid="tx-range-field">
                <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Plage</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-8 rounded-sm font-normal text-xs justify-start px-2 min-w-[180px] max-w-[260px]"
                      data-testid="tx-range-btn"
                    >
                      <CalendarDays size={13} className="mr-1.5 text-zinc-500 flex-shrink-0" />
                      <span className="truncate">
                        {txRange.from && txRange.to
                          ? `${formatDate(txRange.from, "dd MMM yyyy", { locale: frLocale })} → ${formatDate(txRange.to, "dd MMM yyyy", { locale: frLocale })}`
                          : txRange.from
                            ? `Depuis ${formatDate(txRange.from, "dd MMM yyyy", { locale: frLocale })}`
                            : "Toute la période"}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-auto max-w-[95vw] overflow-auto" align="start" data-testid="tx-range-popover">
                    <Calendar
                      mode="range"
                      numberOfMonths={isMobile ? 1 : 2}
                      locale={frLocale}
                      selected={txRange}
                      onSelect={(r) => {
                        if (!r) { setTxRange({ from: undefined, to: undefined }); return; }
                        setTxRange({ from: r.from, to: r.to || r.from });
                      }}
                      initialFocus
                      data-testid="tx-range-calendar"
                    />
                    <div className="flex items-center justify-between gap-2 p-2 border-t border-zinc-200 bg-zinc-50">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setTxRange({ from: undefined, to: undefined })}
                        data-testid="tx-range-reset"
                      >
                        Réinitialiser
                      </Button>
                      <span className="text-[10px] font-mono text-zinc-500">
                        {txRange.from && txRange.to ? "Plage sélectionnée" : "Sélectionnez une plage"}
                      </span>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            )}
          />

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] text-zinc-500 font-mono" data-testid="transactions-count">
              {txSubTab === "pending"
                ? `${pendingPaymentList.length} transaction${pendingPaymentList.length > 1 ? "s" : ""} en attente de paiement`
                : `${totals.count} transaction${totals.count > 1 ? "s" : ""} · ${totals.ventes} vente${totals.ventes > 1 ? "s" : ""} · ${totals.refunds} remboursement${totals.refunds > 1 ? "s" : ""}`}
            </p>
            <ExportCsvButton
              filename={txSubTab === "pending" ? "transactions_en_attente" : "transactions"}
              testid="transactions-export-csv"
              rows={visibleRows}
              columns={[
                { label: "Date", value: (t) => t.date },
                { label: "Type", value: (t) => t.type },
                { label: "Département", value: (t) => t.brand_name || "" },
                { label: "Site", value: (t) => t.site_name || "" },
                { label: "Service / Produit", value: (t) => t.service_product_name || "" },
                { label: "Client", value: (t) => t.client_name || "" },
                { label: "Partenaire", value: (t) => t.partner_name || "" },
                { label: "Collaborateur", value: (t) => t.collaborator_name || "" },
                { label: "HT unitaire", value: (t) => t.unit_price_ht },
                { label: "Quantité", value: (t) => t.quantity },
                { label: "TVA %", value: (t) => t.tva_rate },
                { label: "Code", value: (t) => t.discount_code_value || "" },
                { label: "Remise", value: (t) => t.discount_value ? `${t.discount_value}${t.discount_type === "percentage" ? "%" : "€"}` : "" },
                { label: "TTC", value: (t) => t.amount_ttc },
                { label: "Statut paiement", value: (t) => t.payment_status === "deferred" ? "en attente" : "effectué" },
                { label: "Mode paiement", value: (t) => PAYMENT_METHOD_LABEL[t.payment_method] || PAYMENT_METHOD_LABEL[t.planned_payment_method] || "" },
                { label: "N° réf. paiement", value: (t) => t.payment_reference || "" },
                { label: "Date paiement", value: (t) => t.payment_date || "" },
                { label: "Date règlement prévue", value: (t) => t.planned_payment_date || "" },
              ]}
            />
          </div>

          <HorizontalScrollContainer
            testId="tx-table-scroll"
            className="bg-white border border-zinc-200 rounded-sm"
          >
            <table className="w-full text-sm min-w-[1100px]">
              <thead className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Département</th>
                  <th className="text-left px-3 py-2">Service</th>
                  <th className="text-left px-3 py-2">Client</th>
                  <th className="text-left px-3 py-2">Partenaire</th>
                  <th className="text-left px-3 py-2">Collaborateur</th>
                  <th className="text-left px-3 py-2">Code</th>
                  <th className="text-right px-3 py-2">TTC</th>
                  <th className="text-left px-3 py-2">Paiement</th>
                  <th className="text-left px-3 py-2">Mode</th>
                  <th className="text-left px-3 py-2">Réf.</th>
                  <th className="text-left px-3 py-2">{txSubTab === "pending" ? "Prévu le" : "Date paiement"}</th>
                  {canManage && <th className="px-3 py-2 w-40"></th>}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((t) => (
                  <tr key={t.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`transaction-${t.id}`}>
                    <td className="px-3 py-2 font-mono text-[11px] text-zinc-600">{t.date}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase ${t.type === "vente" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
                        {t.type === "vente" ? <Receipt size={10} /> : <RotateCcw size={10} />}
                        {t.type}
                      </span>
                      {t.refunded && (
                        <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border border-red-200 bg-red-50 text-red-700 text-[10px] font-mono uppercase" title="Cette vente a été remboursée">
                          <RotateCcw size={10} /> remboursée
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-zinc-700">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: t.brand_color || "#F97316" }} />
                        {t.brand_name || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-zinc-700 truncate max-w-[200px]">{t.service_product_name || "—"}</td>
                    <td className="px-3 py-2 text-zinc-700">{t.client_name || "—"}</td>
                    <td className="px-3 py-2 text-zinc-700">
                      {t.partner_name ? (
                        <span className="inline-flex items-center gap-1"><Contact size={11} className="text-zinc-400" />{t.partner_name}</span>
                      ) : <span className="text-zinc-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-zinc-700">
                      {t.collaborator_name ? (
                        <span className="inline-flex items-center gap-1"><Briefcase size={11} className="text-zinc-400" />{t.collaborator_name}</span>
                      ) : <span className="text-zinc-400">—</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px]">
                      {t.discount_code_value ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border border-zinc-200 bg-zinc-50">
                          <Tag size={10} className="text-zinc-500" />
                          {t.discount_code_value}
                        </span>
                      ) : <span className="text-zinc-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-semibold" style={{ color: Number(t.amount_ttc) < 0 ? "#DC2626" : "#0F172A" }}>
                      {money(t.amount_ttc)}
                    </td>
                    <td className="px-3 py-2">
                      {t.payment_status === "deferred" ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border border-amber-200 bg-amber-50 text-amber-700 text-[10px] font-mono uppercase" data-testid={`pay-status-${t.id}`}>
                          <Clock size={10} /> en attente
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px] font-mono uppercase" data-testid={`pay-status-${t.id}`}>
                          <CheckCircle2 size={10} /> effectué
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-zinc-700">
                      {PAYMENT_METHOD_LABEL[t.payment_method] || PAYMENT_METHOD_LABEL[t.planned_payment_method] || <span className="text-zinc-400">—</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-zinc-600">
                      {t.payment_reference || <span className="text-zinc-400">—</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-zinc-600">
                      {t.payment_status === "deferred"
                        ? (t.planned_payment_date || <span className="text-zinc-400">—</span>)
                        : (t.payment_date || <span className="text-zinc-400">—</span>)}
                    </td>
                    {canManage && (
                      <td className="px-3 py-2">
                        <div className="flex gap-1 justify-end">
                          {t.payment_status === "deferred" && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-amber-700 border-amber-300 hover:bg-amber-50 h-7 px-2 text-[10px] font-mono uppercase tracking-wider"
                              onClick={() => openConfirmPayment(t)}
                              title="Valider le paiement de cette transaction"
                              data-testid={`confirm-payment-${t.id}`}
                            >
                              <CheckCircle2 size={11} className="mr-1" />
                              Valider paiement
                            </Button>
                          )}
                          {t.type === "vente" && !t.refunded && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => refund(t)}
                              title="Enregistrer un remboursement pour cette vente"
                              data-testid={`refund-transaction-${t.id}`}
                            ><RotateCcw size={12} /></Button>
                          )}
                          {isAdmin && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`edit-transaction-${t.id}`}><Pencil size={12} /></Button>
                              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(t)} data-testid={`delete-transaction-${t.id}`}><Trash2 size={12} /></Button>
                            </>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={canManage ? 14 : 13} className="px-3 py-8 text-center text-zinc-500 font-mono text-xs">
                      {txSubTab === "pending"
                        ? "Aucune transaction en attente de paiement."
                        : (list.length === 0 ? "Aucune transaction. Enregistrez votre première vente." : "Aucune transaction ne correspond aux filtres.")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </HorizontalScrollContainer>
        </TabsContent>

        {/* ─── COMMISSIONS PARTENAIRES ─── */}
        <TabsContent value="commissions" className="space-y-4 mt-4">
          <CommissionsTab
            allSites={allSites}
            allServices={allServices}
            beneficiaries={allBeneficiaries}
            defaultSubTab={searchParams.get("sub") || "commissions"}
          />
        </TabsContent>

        {/* ─── ROYALTIES ─── */}
        {canSeeRoyalties && (
          <TabsContent value="royalties" className="space-y-4 mt-4">
            <RoyaltiesSection isAdmin={isAdmin} />
          </TabsContent>
        )}

        {/* ─── GOALS ─── */}
        {isAdmin && (
          <TabsContent value="goals" className="space-y-4 mt-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-[11px] text-zinc-500 font-mono">
                Définissez les objectifs de CA net par département (mensuels ou annuels).
              </p>
              <Button onClick={openGoalCreate} className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-sm" data-testid="new-goal-btn">
                <Plus size={14} className="mr-1" /> Nouvel objectif
              </Button>
            </div>

            <HorizontalScrollContainer
              testId="goals-table-scroll"
              className="bg-white border border-zinc-200 rounded-sm"
            >
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                  <tr>
                    <th className="text-left px-3 py-2">Département</th>
                    <th className="text-left px-3 py-2">Périodicité</th>
                    <th className="text-left px-3 py-2">Période</th>
                    <th className="text-right px-3 py-2">Objectif TTC</th>
                    <th className="text-left px-3 py-2">Notes</th>
                    <th className="px-3 py-2 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {goals.map((g) => (
                    <tr key={g.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`goal-${g.id}`}>
                      <td className="px-3 py-2 inline-flex items-center gap-1.5">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: g.brand_color || "#F97316" }} />
                        {g.brand_name || "—"}
                      </td>
                      <td className="px-3 py-2 text-zinc-700 capitalize">{g.period === "month" ? "Mensuel" : "Annuel"}</td>
                      <td className="px-3 py-2 font-mono text-xs text-zinc-600">{g.period_key}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{money(g.target_amount)}</td>
                      <td className="px-3 py-2 text-zinc-500 text-xs truncate max-w-[240px]">{g.notes || "—"}</td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="sm" onClick={() => openGoalEdit(g)} data-testid={`edit-goal-${g.id}`}><Pencil size={12} /></Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removeGoal(g)} data-testid={`delete-goal-${g.id}`}><Trash2 size={12} /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {goals.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-3 py-8 text-center text-zinc-500 font-mono text-xs">
                        Aucun objectif défini pour le moment.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </HorizontalScrollContainer>
          </TabsContent>
        )}
      </Tabs>

      {/* Dialog transaction */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex flex-col max-h-[90vh] p-0 sm:max-w-2xl">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
            <DialogTitle>{editing ? "Modifier la transaction" : "Enregistrer une transaction"}</DialogTitle>
            <DialogDescription className="text-[11px] text-zinc-500 font-mono">
              Le TTC est calculé automatiquement : HT × (1 + TVA) − remise appliquée.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto px-6 py-4 flex-1">
            {/* Département + Date */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="label-uppercase mb-1.5 block">Département</Label>
                <Select
                  value={form.brand_id}
                  onValueChange={(v) => { setForm({ ...form, brand_id: v, site_id: "", service_product_id: "", client_id: "", partner_id: "", collaborator_id: "", discount_code_id: "", discount_code_value: "", discount_code_kind: "none" }); loadBrandContext(v); }}
                >
                  <SelectTrigger data-testid="tx-brand-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>
                    {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-uppercase mb-1.5 block">Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" className="w-full justify-start font-normal" data-testid="tx-date">
                      <CalendarDays size={14} className="mr-2 text-zinc-500" />
                      {form.date ? formatDate(parseIsoDate(form.date), "dd MMM yyyy", { locale: frLocale }) : "Choisir"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="p-0 w-auto">
                    <Calendar mode="single" locale={frLocale} selected={form.date ? parseIsoDate(form.date) : undefined} onSelect={(d) => d && setForm({ ...form, date: formatDate(d, "yyyy-MM-dd") })} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Site + Service */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="label-uppercase mb-1.5 block">Site<span className="text-red-500 ml-0.5">*</span></Label>
                <SearchableSelect
                  value={form.site_id}
                  onChange={(v) => setForm({ ...form, site_id: v })}
                  options={sites.map((s) => ({ value: s.id, label: s.name }))}
                  placeholder={form.brand_id ? "Choisir un site" : "Sélectionnez d'abord un département"}
                  searchPlaceholder="Rechercher un site…"
                  emptyLabel={sites.length === 0 ? "Aucun site pour ce département" : "Aucun résultat."}
                  disabled={!form.brand_id}
                  data-testid="tx-site-select"
                />
              </div>
              <div>
                <Label className="label-uppercase mb-1.5 block">Service / Produit<span className="text-red-500 ml-0.5">*</span></Label>
                <SearchableSelect
                  value={form.service_product_id}
                  onChange={onServiceChange}
                  options={services.map((s) => ({
                    value: s.id,
                    label: s.name,
                    hint: s.tarif_sur_devis ? "Sur devis" : `${Number(s.price_ht || 0).toFixed(2)}€ HT`,
                  }))}
                  placeholder={form.brand_id ? "Choisir" : "Sélectionnez d'abord un département"}
                  searchPlaceholder="Rechercher un service/produit…"
                  emptyLabel={services.length === 0 ? "Aucun service pour ce département" : "Aucun résultat."}
                  disabled={!form.brand_id}
                  data-testid="tx-service-select"
                />
              </div>
            </div>

            {/* Client + Partenaire + Collaborateur */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="label-uppercase mb-1.5 block">Client<span className="text-red-500 ml-0.5">*</span></Label>
                <BeneficiaryCombobox
                  beneficiaries={clients}
                  categoryName="Clientèle"
                  value={form.client_id}
                  onChange={(v) => setForm({ ...form, client_id: v })}
                  placeholder={form.brand_id ? "Choisir" : "Sélectionnez d'abord un département"}
                  searchPlaceholder="Rechercher un client…"
                  disabled={!form.brand_id}
                  onCreateNew={form.brand_id ? () => { setQuickContactTarget("client"); setQuickContactOpen(true); } : undefined}
                  createNewLabel="Nouveau contact"
                  data-testid="tx-client-select"
                />
              </div>
              <div>
                <Label className="label-uppercase mb-1.5 flex items-center gap-1.5">
                  <Contact size={12} className="text-zinc-500" /> Partenaire
                </Label>
                <BeneficiaryCombobox
                  beneficiaries={clients}
                  categoryName="Partenaire"
                  value={form.partner_id || ""}
                  onChange={(v) => setForm({ ...form, partner_id: v || "" })}
                  placeholder={form.brand_id ? "Aucun" : "Sélectionnez un département"}
                  searchPlaceholder="Rechercher un partenaire…"
                  allowClear
                  disabled={!form.brand_id}
                  onCreateNew={form.brand_id ? () => { setQuickContactTarget("partner"); setQuickContactOpen(true); } : undefined}
                  createNewLabel="Nouveau contact"
                  data-testid="tx-partner-select"
                />
              </div>
              <div>
                <Label className="label-uppercase mb-1.5 flex items-center gap-1.5">
                  <Briefcase size={12} className="text-zinc-500" /> Collaborateur
                </Label>
                <SearchableSelect
                  value={form.collaborator_id || ""}
                  onChange={(v) => setForm({ ...form, collaborator_id: v || "" })}
                  options={collabs.map((c) => ({ value: c.id, label: c.name }))}
                  placeholder={form.brand_id ? "Aucun" : "Sélectionnez un département"}
                  searchPlaceholder="Rechercher un collaborateur…"
                  allowClear
                  disabled={!form.brand_id}
                  data-testid="tx-collaborator-select"
                />
              </div>
            </div>
            <p className="text-[11px] text-zinc-500 font-mono -mt-2" data-testid="tx-partner-hint">
              {(() => {
                const s = sites.find((x) => x.id === form.site_id);
                const online = s?.type === "plateforme_en_ligne";
                const codeUsed = !!(form.discount_code_value || "").trim();
                if (online && !codeUsed) {
                  return (
                    <>
                      Site <strong>Plateforme en ligne</strong> — partenaire et collaborateur optionnels tant qu'aucun code de remise n'est utilisé.
                    </>
                  );
                }
                if (online && codeUsed) {
                  return (
                    <>
                      Code de remise saisi — un partenaire <strong>ou</strong> un collaborateur devient obligatoire (chaque code est rattaché à l'un des deux).
                    </>
                  );
                }
                return (
                  <>
                    Au moins un partenaire <strong>ou</strong> un collaborateur doit être renseigné (les deux peuvent être combinés).
                  </>
                );
              })()}
            </p>

            {/* HT / Quantité / TVA */}
            {(() => {
              const selectedService = services.find((s) => s.id === form.service_product_id);
              const svcIsQuote = !!(selectedService && selectedService.tarif_sur_devis);
              return (
                <>
                  {svcIsQuote && (
                    <div
                      className="rounded-sm border border-amber-300 bg-amber-50/70 px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-amber-900 flex items-center gap-2"
                      data-testid="tx-quote-banner"
                    >
                      <Tag size={12} />
                      Tarif sur devis — saisissez manuellement Prix HT, TVA (%), Frais HT facturés et Débours facturés.
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="label-uppercase mb-1.5 block">
                        HT unitaire (€){svcIsQuote && <span className="text-red-500 ml-0.5">*</span>}
                      </Label>
                      <Input type="number" min="0" step="0.01" value={form.unit_price_ht} onChange={(e) => setForm({ ...form, unit_price_ht: e.target.value })} data-testid="tx-ht" />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Quantité</Label>
                      <Input type="number" min="0.01" step="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="tx-qty" />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">
                        TVA (%){svcIsQuote && <span className="text-red-500 ml-0.5">*</span>}
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={form.tva_rate}
                        onChange={svcIsQuote ? ((e) => setForm({ ...form, tva_rate: e.target.value })) : undefined}
                        readOnly={!svcIsQuote}
                        disabled={!svcIsQuote}
                        className={svcIsQuote ? "" : "bg-zinc-50 text-zinc-700 cursor-not-allowed"}
                        title={svcIsQuote ? "Service au tarif sur devis — TVA à saisir" : "Le taux de TVA est repris automatiquement du service ou produit sélectionné"}
                        data-testid="tx-tva"
                      />
                      <p className="text-[10px] text-zinc-500 font-mono mt-1">
                        {svcIsQuote
                          ? "Service sur devis — TVA à saisir manuellement"
                          : (form.service_product_id
                            ? "Repris automatiquement du service/produit sélectionné"
                            : "Sélectionnez un service/produit pour renseigner la TVA")}
                      </p>
                    </div>
                  </div>
                  {svcIsQuote && (
                    <div className="grid grid-cols-2 gap-3" data-testid="tx-quote-extra-fields">
                      <div>
                        <Label className="label-uppercase mb-1.5 block">Frais HT facturés (€)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={form.fees_ht}
                          onChange={(e) => setForm({ ...form, fees_ht: e.target.value })}
                          placeholder="0.00"
                          data-testid="tx-fees-ht"
                        />
                        <p className="text-[10px] text-zinc-500 font-mono mt-1">Optionnel — soumis à la TVA (avec le Tarif HT)</p>
                      </div>
                      <div>
                        <Label className="label-uppercase mb-1.5 block">Débours facturés (€)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={form.disbursements}
                          onChange={(e) => setForm({ ...form, disbursements: e.target.value })}
                          placeholder="0.00"
                          data-testid="tx-disbursements"
                        />
                        <p className="text-[10px] text-zinc-500 font-mono mt-1">Optionnel — jamais soumis à la TVA</p>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Code : Select + saisie */}
            <div className="rounded-sm border border-[#F97316]/30 bg-[#F97316]/5 p-3 space-y-3">
              <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1.5">
                <Tag size={12} /> Code de remise (optionnel)
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] text-zinc-500 mb-1 block">Choisir dans la liste des codes actifs</Label>
                  <Select value={form.discount_code_id ? `${form.discount_code_kind}:${form.discount_code_id}` : "none"} onValueChange={applyActiveCode} disabled={!form.brand_id}>
                    <SelectTrigger data-testid="tx-code-select"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun code</SelectItem>
                      {activeCodes.map((c) => (
                        <SelectItem key={`${c.kind}:${c.id}`} value={`${c.kind}:${c.id}`}>
                          [{c.kind.slice(0, 3).toUpperCase()}] {c.code} · {c.discount_value}{c.discount_type === "percentage" ? "%" : "€"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-zinc-500 mb-1 block">Ou saisir le code manuellement</Label>
                  <Input
                    value={form.discount_code_value}
                    onChange={(e) => { setForm({ ...form, discount_code_value: e.target.value.toUpperCase() }); setCodeStatus({ msg: "", cls: "" }); }}
                    onBlur={(e) => e.target.value.trim() && resolveTypedCode(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (e.target.value.trim()) resolveTypedCode(e.target.value); } }}
                    placeholder="Ex : PROMO-XXXXXX"
                    style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}
                    data-testid="tx-code-input"
                    disabled={codeLookup}
                  />
                </div>
              </div>
              {codeStatus.msg && (
                <p className={`text-[11px] font-mono ${codeStatus.cls}`} data-testid="tx-code-status">{codeStatus.msg}</p>
              )}
              {/* Sélecteur d'offre — visible si le code choisi propose plusieurs offres */}
              {(() => {
                const c = activeCodes.find((x) => x.id === form.discount_code_id)
                       || (typedCodeCache && typedCodeCache.id === form.discount_code_id ? typedCodeCache : null);
                const offers = Array.isArray(c?.offers) ? c.offers : [];
                if (!c || offers.length === 0) return null;
                const compat = (o) => !(o.service_product_ids || []).length || (o.service_product_ids || []).includes(form.service_product_id);
                return (
                  <div data-testid="tx-offer-picker" className="pt-2 border-t border-[#F97316]/20">
                    <Label className="text-[10px] text-zinc-500 mb-1 block inline-flex items-center gap-1.5">
                      <Tag size={11} /> Offre applicable
                    </Label>
                    <Select
                      value={form.discount_offer_id || "none"}
                      onValueChange={(v) => { if (v === "none") { setForm((f) => ({ ...f, discount_offer_id: "", discount_type: "none", discount_value: 0 })); } else { applyPickedOffer(v); } }}
                    >
                      <SelectTrigger data-testid="tx-offer-select"><SelectValue placeholder="Sélectionnez une offre" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Sélectionnez une offre —</SelectItem>
                        {offers.map((o) => {
                          const ok = compat(o);
                          const nameList = (o.service_products || []).map((s) => s.name).filter(Boolean);
                          const scope = nameList.length === 0
                            ? "Tous services"
                            : (nameList.length > 2 ? `${nameList.slice(0, 2).join(", ")} +${nameList.length - 2}` : nameList.join(", "));
                          const label = o.label ? `${o.label} · ` : "";
                          return (
                            <SelectItem key={o.id} value={o.id} disabled={!ok}>
                              {label}{o.discount_value}{o.discount_type === "percentage" ? "%" : "€"} · {scope}{ok ? "" : " (non applicable)"}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })()}
            </div>

            {/* Autre remise accordée — remise manuelle additionnelle appliquée sur le HT */}
            <div className="rounded-sm border border-purple-500/30 bg-purple-50/40 p-3 space-y-3" data-testid="tx-extra-discount-block">
              <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1.5">
                <Percent size={12} /> Autre remise accordée (optionnel)
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-[10px] text-zinc-500 mb-1 block">Type</Label>
                  <Select
                    value={form.extra_discount_type || "none"}
                    onValueChange={(v) => setForm({ ...form, extra_discount_type: v, extra_discount_value: v === "none" ? 0 : form.extra_discount_value })}
                  >
                    <SelectTrigger data-testid="tx-extra-discount-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune</SelectItem>
                      <SelectItem value="percentage">Pourcentage (%)</SelectItem>
                      <SelectItem value="amount">Montant (€ HT)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-zinc-500 mb-1 block">
                    Valeur {form.extra_discount_type === "amount" ? "(€)" : form.extra_discount_type === "percentage" ? "(%)" : ""}
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    max={form.extra_discount_type === "percentage" ? 100 : undefined}
                    value={form.extra_discount_value}
                    onChange={(e) => setForm({ ...form, extra_discount_value: parseFloat(e.target.value) || 0 })}
                    disabled={form.extra_discount_type === "none"}
                    placeholder="0"
                    data-testid="tx-extra-discount-value"
                  />
                </div>
                <div className="sm:col-span-1">
                  <Label className="text-[10px] text-zinc-500 mb-1 block">Motif (optionnel)</Label>
                  <Input
                    value={form.extra_discount_note}
                    onChange={(e) => setForm({ ...form, extra_discount_note: e.target.value })}
                    placeholder="ex : geste commercial"
                    disabled={form.extra_discount_type === "none"}
                    data-testid="tx-extra-discount-note"
                  />
                </div>
              </div>
              {form.extra_discount_type !== "none" && (
                <p className="text-[11px] font-mono text-purple-800/80" data-testid="tx-extra-discount-help">
                  Cette remise est cumulée avec la remise du code (dans la limite du Tarif HT) et déduite du calcul TTC.
                </p>
              )}
            </div>

            {/* Aperçu TTC */}
            <div className="rounded-sm border border-zinc-200 bg-zinc-50 px-4 py-3 grid grid-cols-3 gap-3 text-center" data-testid="tx-preview">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Sous-total HT</p>
                <p className="text-sm font-mono font-semibold">{money(previewTTC.ht)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Remise</p>
                <p className="text-sm font-mono font-semibold text-[#F97316]">− {money(previewTTC.discount)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">TTC final</p>
                <p className="text-lg font-mono font-bold" style={{ color: previewTTC.ttc < 0 ? "#DC2626" : "#059669" }}>{money(previewTTC.ttc)}</p>
              </div>
            </div>

            {/* Paiement — statut + champs conditionnels (masqué pour un remboursement) */}
            {form.type !== "remboursement" && (
              <div className="rounded-sm border border-emerald-500/30 bg-emerald-50/40 p-3 space-y-3" data-testid="tx-payment-block">
                <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1.5">
                  <Wallet size={12} /> Paiement
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label
                    className={`cursor-pointer rounded-sm border p-2.5 flex items-start gap-2 transition-colors ${
                      form.payment_status === "paid" ? "border-emerald-500 bg-white shadow-sm" : "border-zinc-200 bg-white/60 hover:bg-white"
                    }`}
                    data-testid="tx-pay-status-paid"
                  >
                    <input
                      type="radio"
                      name="payment_status"
                      value="paid"
                      checked={form.payment_status === "paid"}
                      onChange={() => setForm({ ...form, payment_status: "paid" })}
                      className="mt-0.5 accent-emerald-600"
                    />
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-zinc-800 inline-flex items-center gap-1.5">
                        <CheckCircle2 size={12} className="text-emerald-600" /> Paiement effectué
                      </div>
                      <p className="text-[10px] font-mono text-zinc-500 mt-0.5">Le règlement a déjà été perçu.</p>
                    </div>
                  </label>
                  <label
                    className={`cursor-pointer rounded-sm border p-2.5 flex items-start gap-2 transition-colors ${
                      form.payment_status === "deferred" ? "border-amber-500 bg-white shadow-sm" : "border-zinc-200 bg-white/60 hover:bg-white"
                    }`}
                    data-testid="tx-pay-status-deferred"
                  >
                    <input
                      type="radio"
                      name="payment_status"
                      value="deferred"
                      checked={form.payment_status === "deferred"}
                      onChange={() => setForm({ ...form, payment_status: "deferred" })}
                      className="mt-0.5 accent-amber-600"
                    />
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-zinc-800 inline-flex items-center gap-1.5">
                        <Clock size={12} className="text-amber-600" /> Paiement différé
                      </div>
                      <p className="text-[10px] font-mono text-zinc-500 mt-0.5">Le règlement est prévu ultérieurement.</p>
                    </div>
                  </label>
                </div>

                {form.payment_status === "paid" ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Mode de paiement<span className="text-red-500 ml-0.5">*</span></Label>
                      <Select value={form.payment_method || "cb"} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                        <SelectTrigger data-testid="tx-payment-method"><SelectValue placeholder="Choisir" /></SelectTrigger>
                        <SelectContent>
                          {PAYMENT_METHODS.map((m) => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">N° ID transaction</Label>
                      <Input
                        value={form.payment_reference}
                        onChange={(e) => setForm({ ...form, payment_reference: e.target.value })}
                        placeholder="Ex : REF-42, ticket #123…"
                        style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}
                        data-testid="tx-payment-reference"
                      />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Date de paiement<span className="text-red-500 ml-0.5">*</span></Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button type="button" variant="outline" className="w-full justify-start font-normal" data-testid="tx-payment-date">
                            <CalendarDays size={14} className="mr-2 text-zinc-500" />
                            {form.payment_date ? formatDate(parseIsoDate(form.payment_date), "dd MMM yyyy", { locale: frLocale }) : "Choisir"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="p-0 w-auto">
                          <Calendar mode="single" locale={frLocale} selected={form.payment_date ? parseIsoDate(form.payment_date) : undefined} onSelect={(d) => d && setForm({ ...form, payment_date: formatDate(d, "yyyy-MM-dd") })} initialFocus />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Date de règlement prévue<span className="text-red-500 ml-0.5">*</span></Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button type="button" variant="outline" className="w-full justify-start font-normal" data-testid="tx-planned-payment-date">
                            <CalendarDays size={14} className="mr-2 text-zinc-500" />
                            {form.planned_payment_date ? formatDate(parseIsoDate(form.planned_payment_date), "dd MMM yyyy", { locale: frLocale }) : "Choisir"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="p-0 w-auto">
                          <Calendar mode="single" locale={frLocale} selected={form.planned_payment_date ? parseIsoDate(form.planned_payment_date) : undefined} onSelect={(d) => d && setForm({ ...form, planned_payment_date: formatDate(d, "yyyy-MM-dd") })} initialFocus />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Mode de règlement prévu<span className="text-red-500 ml-0.5">*</span></Label>
                      <Select value={form.planned_payment_method || "transfer"} onValueChange={(v) => setForm({ ...form, planned_payment_method: v })}>
                        <SelectTrigger data-testid="tx-planned-payment-method"><SelectValue placeholder="Choisir" /></SelectTrigger>
                        <SelectContent>
                          {PAYMENT_METHODS.map((m) => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <Label className="label-uppercase mb-1.5 block">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Facultatif" data-testid="tx-notes" />
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} className="bg-[#F97316] hover:bg-[#EA580C] text-white" data-testid="tx-save-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog objectif */}
      <Dialog open={goalOpen} onOpenChange={setGoalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="goal-dialog">
          <DialogHeader>
            <DialogTitle>{goalEditing ? "Modifier l'objectif" : "Nouvel objectif de CA"}</DialogTitle>
            <DialogDescription className="text-[11px] text-zinc-500 font-mono">
              Objectif TTC à atteindre pour un département sur une période donnée.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="label-uppercase mb-1.5 block">Département</Label>
              <Select value={goalForm.brand_id} onValueChange={(v) => setGoalForm({ ...goalForm, brand_id: v })} disabled={!!goalEditing}>
                <SelectTrigger data-testid="goal-brand-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>
                  {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-uppercase mb-1.5 block">Périodicité</Label>
                <Select
                  value={goalForm.period}
                  onValueChange={(v) => setGoalForm({
                    ...goalForm,
                    period: v,
                    period_key: v === "year" ? String(new Date().getFullYear()) : new Date().toISOString().slice(0, 7),
                  })}
                  disabled={!!goalEditing}
                >
                  <SelectTrigger data-testid="goal-period-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">Mensuel</SelectItem>
                    <SelectItem value="year">Annuel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-uppercase mb-1.5 block">Période</Label>
                <Input
                  value={goalForm.period_key}
                  onChange={(e) => setGoalForm({ ...goalForm, period_key: e.target.value })}
                  placeholder={goalForm.period === "year" ? "2026" : "2026-02"}
                  data-testid="goal-period-key"
                  disabled={!!goalEditing}
                />
              </div>
            </div>
            <div>
              <Label className="label-uppercase mb-1.5 block">Objectif TTC (€)</Label>
              <Input
                type="number" min="0" step="0.01"
                value={goalForm.target_amount}
                onChange={(e) => setGoalForm({ ...goalForm, target_amount: e.target.value })}
                data-testid="goal-target-amount"
              />
            </div>
            <div>
              <Label className="label-uppercase mb-1.5 block">Notes</Label>
              <Textarea rows={2} value={goalForm.notes} onChange={(e) => setGoalForm({ ...goalForm, notes: e.target.value })} data-testid="goal-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGoalOpen(false)}>Annuler</Button>
            <Button onClick={saveGoal} className="bg-[#F97316] hover:bg-[#EA580C] text-white" data-testid="goal-save-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog confirmation de paiement (transaction différée → basculée en effectuée) */}
      <Dialog open={confirmPayOpen} onOpenChange={setConfirmPayOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-2">
              <CheckCircle2 size={16} className="text-emerald-600" /> Valider le paiement
            </DialogTitle>
            <DialogDescription className="text-[11px] text-zinc-500 font-mono">
              La transaction basculera automatiquement dans « Transactions » (paiement effectué).
            </DialogDescription>
          </DialogHeader>
          {confirmPayTx && (
            <div className="text-[11px] font-mono text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2">
              {confirmPayTx.date} · <strong>{confirmPayTx.client_name || "—"}</strong> · {money(confirmPayTx.amount_ttc)}
              {confirmPayTx.planned_payment_date && (
                <span className="ml-2 text-amber-700">Prévu le {confirmPayTx.planned_payment_date}</span>
              )}
            </div>
          )}
          <div className="space-y-3">
            <div>
              <Label className="label-uppercase mb-1.5 block">Date effective du paiement<span className="text-red-500 ml-0.5">*</span></Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" className="w-full justify-start font-normal" data-testid="confirm-pay-date">
                    <CalendarDays size={14} className="mr-2 text-zinc-500" />
                    {confirmPayForm.payment_date ? formatDate(parseIsoDate(confirmPayForm.payment_date), "dd MMM yyyy", { locale: frLocale }) : "Choisir"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-0 w-auto">
                  <Calendar mode="single" locale={frLocale} selected={confirmPayForm.payment_date ? parseIsoDate(confirmPayForm.payment_date) : undefined} onSelect={(d) => d && setConfirmPayForm({ ...confirmPayForm, payment_date: formatDate(d, "yyyy-MM-dd") })} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="label-uppercase mb-1.5 block">Mode de paiement effectif<span className="text-red-500 ml-0.5">*</span></Label>
              <Select value={confirmPayForm.payment_method} onValueChange={(v) => setConfirmPayForm({ ...confirmPayForm, payment_method: v })}>
                <SelectTrigger data-testid="confirm-pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-uppercase mb-1.5 block">N° ID transaction</Label>
              <Input
                value={confirmPayForm.payment_reference}
                onChange={(e) => setConfirmPayForm({ ...confirmPayForm, payment_reference: e.target.value })}
                placeholder="Ex : REF-42, ticket #123…"
                style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}
                data-testid="confirm-pay-reference"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmPayOpen(false)}>Annuler</Button>
            <Button
              onClick={submitConfirmPayment}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="confirm-pay-submit"
            >
              <CheckCircle2 size={13} className="mr-1.5" /> Valider le paiement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale de création rapide d'un contact (nested au-dessus de la modale transaction) */}
      <QuickBeneficiaryDialog
        open={quickContactOpen}
        onOpenChange={setQuickContactOpen}
        brandId={form.brand_id}
        defaultCategory={quickContactTarget === "partner" ? "Partenaire" : "Clientèle"}
        onCreated={(newContact) => {
          if (!newContact?.id) return;
          setClients((prev) => [...prev, newContact]);
          setForm((f) => (
            quickContactTarget === "partner"
              ? { ...f, partner_id: newContact.id }
              : { ...f, client_id: newContact.id }
          ));
        }}
      />
    </div>
  );
}

function PayoutCard({ label, amount, sub, icon, accent, testid }) {
  return (
    <div
      className="bg-white border border-zinc-200 rounded-sm p-4 flex items-start gap-3"
      data-testid={testid}
      style={{ borderLeft: `3px solid ${accent}` }}
    >
      <span
        className="h-9 w-9 rounded-sm text-white inline-flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: accent }}
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{label}</p>
        <p className="text-2xl font-mono font-semibold mt-0.5" style={{ color: accent }}>
          {Number(amount || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
        </p>
        <p className="text-[10px] text-zinc-500 font-mono mt-1">{sub}</p>
      </div>
    </div>
  );
}

function FinancialCard({ label, amount, previous, icon, accent, testid, helper }) {
  const cur = Number(amount || 0);
  const prev = Number(previous || 0);
  const diff = cur - prev;
  let pct = 0;
  if (prev === 0) pct = cur > 0 ? 100 : 0;
  else pct = Math.round(((cur - prev) / Math.abs(prev)) * 1000) / 10;
  const up = diff >= 0;
  return (
    <div
      className="bg-white border border-zinc-200 rounded-sm p-4 flex items-start gap-3"
      data-testid={testid}
      style={{ borderLeft: `3px solid ${accent}` }}
    >
      <span
        className="h-9 w-9 rounded-sm text-white inline-flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: accent }}
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{label}</p>
        <p className="text-2xl font-mono font-semibold mt-0.5" style={{ color: accent }}>
          {cur.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
        </p>
        <div className="text-[10px] font-mono mt-1 flex items-center gap-2 text-zinc-500">
          <span className={up ? "text-emerald-600" : "text-red-600"}>
            {up ? "+" : ""}{pct}%
          </span>
          <span className="text-zinc-400">
            Précédent : {prev.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
          </span>
        </div>
        {helper && <p className="text-[10px] text-zinc-400 font-mono mt-0.5">{helper}</p>}
      </div>
    </div>
  );
}


function ComparisonCard({ label, current, previous, tint, invert = false, testid, serverPct }) {
  const diff = current - previous;
  let pct = 0;
  if (serverPct !== undefined && serverPct !== null) {
    pct = serverPct;
  } else if (previous === 0) {
    pct = current > 0 ? 100 : 0;
  } else {
    pct = Math.round(((current - previous) / Math.abs(previous)) * 1000) / 10;
  }
  const isPositive = invert ? diff < 0 : diff > 0;
  const isFlat = Math.abs(diff) < 0.01;

  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid={testid} style={{ borderLeft: `3px solid ${tint}` }}>
      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{label}</p>
      <p className="text-2xl font-mono font-semibold mt-1" style={{ color: tint }}>
        {`${Number(current || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`}
      </p>
      <div className="mt-2 flex items-center justify-between text-[10px] font-mono">
        <span className="text-zinc-400">Précédent: {`${Number(previous || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}€`}</span>
        <span className={`inline-flex items-center gap-0.5 ${isFlat ? "text-zinc-500" : isPositive ? "text-emerald-600" : "text-red-600"}`}>
          {isFlat ? "—" : (isPositive ? <TrendingUp size={10} /> : <TrendingDown size={10} />)}
          {isFlat ? "0%" : `${pct > 0 ? "+" : ""}${pct}%`}
        </span>
      </div>
    </div>
  );
}
